import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ModeToggle } from "./ModeToggle";
import { OndaAvatar } from "./OndaAvatar";
import { VerifiedBadge } from "./VerifiedBadge";
import { QLogo } from "./branding/QLogo";
import { NexaTokensChip } from "./NexaTokensChip";
import { SOSToggleButton } from "./sos/SOSToggleButton";
import { PreparedBadge } from "./badges/PreparedBadge";
import { usePreparedBadge } from "../hooks/usePreparedBadge";
import { LogOut, Menu, User as UserIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function Header({ onOpenDrawer }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(false);
  const prepared = usePreparedBadge(user?.emergency_profile_complete);

  // One-shot burst on the tokens chip whenever a share event fires OR the
  // balance actually increased via WS (referral credit / welcome bonus).
  useEffect(() => {
    const onSharePulse = () => {
      setPulse(false);
      setTimeout(() => setPulse(true), 20);
      setTimeout(() => setPulse(false), 1400);
    };
    const onTokensUpdate = (e) => {
      const delta = e?.detail?.delta;
      if (typeof delta === "number" && delta > 0) onSharePulse();
    };
    window.addEventListener("queonda.tokens.pulse", onSharePulse);
    window.addEventListener("queonda.tokens.update", onTokensUpdate);
    return () => {
      window.removeEventListener("queonda.tokens.pulse", onSharePulse);
      window.removeEventListener("queonda.tokens.update", onTokensUpdate);
    };
  }, []);

  return (
    <header
      data-testid="app-header"
      className="fixed top-0 inset-x-0 z-40 q-glass border-b border-white/10"
      style={{
        transition: "background-color 500ms ease, border-color 500ms ease",
        paddingTop: "max(16px, env(safe-area-inset-top, 20px))",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <div className="max-w-2xl mx-auto flex items-center justify-between px-4 pb-3">
        <div className="flex items-center gap-2 min-w-0">
          {user && onOpenDrawer && (
            <button
              type="button"
              onClick={onOpenDrawer}
              data-testid="drawer-trigger"
              aria-label="Abrir navegación"
              className="p-2 rounded-xl transition-colors hover:bg-white/5 text-slate-200 hover:text-slate-100"
            >
              <Menu size={20} strokeWidth={2.3} />
            </button>
          )}
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 group min-w-0">
            <span
              className="inline-flex items-center justify-center shrink-0 transition-transform group-hover:rotate-[-6deg]"
              aria-hidden="true"
            >
              <QLogo size={38} />
            </span>
            <div className="leading-tight min-w-0">
              <div className="font-display text-lg font-bold tracking-wide truncate">
                <span className="notranslate" translate="no">
                  Qué Onda <span className="q-accent-text">Chat</span>
                </span>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-slate-500 truncate">
                red cibernauta
              </div>
            </div>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <SOSToggleButton />
          {user && (
            <NexaTokensChip
              balance={user.nexatokens_balance || 0}
              pulse={pulse}
              className="hidden sm:inline-flex"
            />
          )}
          <ModeToggle />
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="q-ring-accent rounded-2xl transition-transform hover:scale-105"
                  aria-label="Menú de usuario"
                >
                  <OndaAvatar
                    src={user.avatar}
                    name={user.display_name}
                    size={38}
                    testId="header-avatar"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-56 border-white/10 bg-[#111c33]/95 backdrop-blur-xl text-slate-100"
              >
                <DropdownMenuLabel className="font-mono text-xs text-slate-400 flex items-center gap-1">
                  @{user.ondatag}
                  {user.is_verified_business && <VerifiedBadge size={11} />}
                </DropdownMenuLabel>
                {prepared && (
                  <div className="px-2 pb-2" data-testid="menu-prepared-wrap">
                    <PreparedBadge compact testId="menu-prepared-badge" />
                  </div>
                )}
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="menu-profile"
                  onSelect={() => navigate("/perfil")}
                  className="cursor-pointer focus:bg-white/5"
                >
                  <UserIcon size={16} className="mr-2" /> Mi perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem
                  data-testid="menu-logout"
                  onSelect={() => {
                    logout();
                    navigate("/auth");
                  }}
                  className="cursor-pointer focus:bg-white/5"
                >
                  <LogOut size={16} className="mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
